import { z } from 'zod';

import { apiRequest } from '@/api/client';

/**
 * The marketing pages' visit counter, mirroring `app/api/v1/site_visits.py`.
 *
 * Anybody may post it. The address, browser and location are worked out by the
 * server from the request itself, so the body carries only what the page alone
 * knows: where the visitor landed and what sent them. Visits are read in the
 * platform panel.
 */

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

export type SiteVisitPayload = Partial<
  Record<'path' | 'referrer' | (typeof UTM_KEYS)[number], string>
>;

/** What the visit looks like from inside the page, with empty values left out. */
export function siteVisitPayload(
  location: Pick<Location, 'pathname' | 'search'> = window.location,
  referrer: string = document.referrer,
): SiteVisitPayload {
  const payload: SiteVisitPayload = {};
  if (location.pathname) payload.path = location.pathname;
  if (referrer) payload.referrer = referrer;

  const params = new URLSearchParams(location.search);
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) payload[key] = value;
  }
  return payload;
}

export function recordSiteVisit(payload: SiteVisitPayload) {
  return apiRequest('/site-visits', z.object({ recorded: z.boolean() }), {
    method: 'POST',
    body: payload,
    // A visitor, not a session: no token to send and none to renew.
    auth: 'none',
    skipAuthRefresh: true,
    // Lets the request finish if the visitor leaves before it has.
    keepalive: true,
  });
}
